import { AfterViewChecked, Component, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { ClassOfServiceService } from '../class-of-service.service';
import { AddCosComponent } from '../add-cos/add-cos.component';
import { ClassOfServiceTo } from '../../shared/models/classOfServiceTo';
import { CosDataTo } from '../../shared/models/cosDataTo';
import { SharedService } from '../../shared/shared.service';
import { HomeService } from '../../home/home.service';
import { Alerts } from '../../shared/enums/alerts';

import Swal from 'sweetalert2/dist/sweetalert2.js';

@Component({
    selector: 'app-list-cos',
    templateUrl: './list-cos.component.html',
    styleUrls: ['./list-cos.component.scss']
})
export class ListCosComponent implements OnInit, AfterViewChecked {

    classOfService: ClassOfServiceTo = new ClassOfServiceTo();
    recordExists = false;
    isLoading = true;
    displayFailureMsg = false;
    displayedColumns: string[] = ['cos', 'callType', 'locationType', 'options'];
    dataSource = new MatTableDataSource<CosDataTo>([]);
    orgId: string = null;
    orgName: string = null;
    sort: MatSort;
    dialogRef;
    @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator;
    paginatorLength: number;
    // The bottom paginator's bindings read the `paginator` ViewChild above directly
    // (paginator?.pageSizeOptions, etc). That ViewChild only exists once *ngIf="!isLoading"
    // renders the top paginator, which happens well after the component's single
    // ngAfterViewInit call (data loads asynchronously). So we can't flip a "ready" flag in
    // ngAfterViewInit - it fires too early, before the ViewChild is ever populated. Instead
    // ngAfterViewChecked (which runs after every check) watches for `paginator` to actually
    // become truthy, then defers flipping the flag to the next tick so the bottom paginator's
    // first render lands in a fresh cycle where `paginator` is already stable - otherwise the
    // same-cycle undefined -> populated flip is exactly what caused
    // ExpressionChangedAfterItHasBeenCheckedError.
    viewInitialized = false;
    private viewInitScheduled = false;

    // The table (and its MatSort directive) is only present in the DOM once loading
    // finishes, since it sits behind *ngIf="!isLoading". A plain @ViewChild(..., {static:
    // true}) would resolve before that and never see the sort instance, so use a setter
    // that re-attaches sort to the current dataSource whenever the element (re)appears.
    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this.sort = sort;
        if (sort) {
            // Assigning dataSource.sort recomputes filteredData/renderedData immediately, which
            // other bindings in this same template read. Doing that synchronously here (during
            // Angular's view-query-check phase) is what was triggering
            // ExpressionChangedAfterItHasBeenCheckedError, so defer it to the next tick.
            setTimeout(() => this.dataSource.sort = sort);
        }
    }

    constructor(private classOfServiceService: ClassOfServiceService,
                protected sharedService: SharedService,
                private homeService: HomeService,
                private dialog: MatDialog) {
    }

    close() {
        this.homeService.navigate.next('listOrganization');
    }

    setDataSource(data: CosDataTo[]) {
        this.dataSource = new MatTableDataSource<CosDataTo>(data);
        this.dataSource.sort = this.sort;
        this.dataSource.filterPredicate = (row: CosDataTo, filter: string) =>
            (row.cos || '').trim().toLowerCase().indexOf(filter) !== -1 ||
            (row.callType || '').trim().toLowerCase().indexOf(filter) !== -1 ||
            (row.locationType || '').trim().toLowerCase().indexOf(filter) !== -1;
        this.paginatorLength = data.length > 20 ? data.length : 20;
        // Deferred: the paginator only exists once *ngIf="!isLoading" renders the table, which
        // happens after this call returns, so the ViewChild isn't populated yet on this tick.
        setTimeout(() => this.dataSource.paginator = this.paginator);
    }

    applyFilter(filterValue: string) {
        this.dataSource.filter = filterValue.trim().toLowerCase();
    }

    syncPrimaryPaginator(event: PageEvent) {
        this.paginator.pageIndex = event.pageIndex;
        this.paginator.pageSize = event.pageSize;
        this.paginator.page.emit(event);
    }

    ngAfterViewChecked(): void {
        if (this.paginator && !this.viewInitialized && !this.viewInitScheduled) {
            this.viewInitScheduled = true;
            setTimeout(() => this.viewInitialized = true);
        }
    }

    ngOnInit(): void {
        // Reached either from the top-nav "CoS" menu item (global, no org context) or from a
        // "Class of Service" icon on an organization row (scoped to that org's clientId). Both
        // producers push {orgId, navigate: 'listCos'} through this shared BehaviorSubject.
        this.sharedService.currentOrgId$.subscribe(data => {
            if (data?.navigate === 'listCos') {
                this.orgId = data.orgId || null;
                this.orgName = data.orgName || null;
                this.loadClassOfService();
            }
        });
    }

    loadClassOfService() {
        this.isLoading = true;
        this.displayFailureMsg = false;
        this.classOfServiceService.getClassOfService(this.orgId).subscribe({
            next: data => {
                this.classOfService = data;
                this.recordExists = true;
                this.setDataSource(this.classOfService.data);
                this.isLoading = false;
            },
            error: error => {
                this.isLoading = false;
                if (error.status === 404) {
                    this.classOfService = new ClassOfServiceTo({ global: !this.orgId, orgId: this.orgId, data: [] });
                    this.recordExists = false;
                    this.setDataSource([]);
                } else {
                    this.displayFailureMsg = true;
                }
            }
        });
    }

    openAddDialog() {
        this.dialogRef = this.dialog.open(AddCosComponent, {
            data: { existingCodes: this.classOfService.data.map(d => d.cos) }
        });
        this.dialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.classOfService.data.push(new CosDataTo(result));
                this.save('Class of Service entry added');
            }
        });
    }

    deleteRow(row: CosDataTo) {
        Swal.fire({
            title: 'Are you sure?',
            text: 'Delete the CoS entry "' + row.cos + '"',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#DD6B55',
            confirmButtonText: 'Yes, Delete',
            cancelButtonText: 'No, cancel'
        }).then((result: any) => {
            if (result.value) {
                const index = this.classOfService.data.indexOf(row);
                if (index > -1) {
                    this.classOfService.data.splice(index, 1);
                }
                this.save('CoS entry "' + row.cos + '" has been deleted.');
            }
        });
    }

    save(successMessage: string) {
        const payload = { ...this.classOfService, global: !this.orgId, orgId: this.orgId };
        const save$ = this.recordExists
            ? this.classOfServiceService.updateClassOfService(payload)
            : this.classOfServiceService.createClassOfService(payload);
        save$.subscribe({
            next: data => {
                this.classOfService = data;
                this.recordExists = true;
                this.setDataSource(this.classOfService.data);
                this.sharedService.showAlert(Alerts.SUCCESS, successMessage);
            },
            error: error => {
                this.sharedService.showAlert(Alerts.ERROR, error.error?.message || 'Unable to save Class of Service');
                this.loadClassOfService();
            }
        });
    }
}
